import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Check, ChevronRight, ShieldCheck, Zap, Download, PlayCircle,
  Users, Sparkles, HelpCircle, Package, Layers,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { trackEvent } from "../lib/tracking";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { ProductCard } from "./ProductsPage";

const Section = ({ icon: Icon, title, children, testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5 }}
    className="py-8 border-t border-white/5"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-6">
      {Icon && (
        <div className="p-2 bg-purple-500/10 rounded-lg">
          <Icon className="w-5 h-5 text-purple-400" />
        </div>
      )}
      <h2 className="text-2xl font-bold text-white">{title}</h2>
    </div>
    {children}
  </motion.section>
);

function loadCashfree() {
  return new Promise((resolve) => {
    if (window.Cashfree) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoCheckoutStarted = useRef(false);
  const checkoutLockRef = useRef(false);
  const cashfreeRef = useRef(null);
  const pollTimerRef = useRef(null);
  const timeoutTimerRef = useRef(null);
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [license, setLicense] = useState("creator");
  const [activeImg, setActiveImg] = useState(0);
  const [buying, setBuying] = useState(false);
  const [customerPhone, setCustomerPhone] = useState(user?.phone || "");
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [pendingCheckoutLicense, setPendingCheckoutLicense] = useState(null);

  useEffect(() => {
    if (user?.phone) setCustomerPhone(user.phone);
  }, [user?.phone]);

  useEffect(() => {
    return () => {
      window.clearInterval(pollTimerRef.current);
      window.clearTimeout(timeoutTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        const defaultLicense = r.data.licenses?.creator?.enabled ? "creator" : "reseller";
        const trackedLicense = r.data.licenses?.[defaultLicense] || {};
        trackEvent("ViewContent", {
          content_ids: [r.data.id],
          content_name: r.data.name,
          content_type: "product",
          value: Number(trackedLicense.price || 0),
          currency: "INR",
          items: [
            {
              item_id: r.data.id,
              item_name: r.data.name,
              item_category: r.data.category_name,
              price: Number(trackedLicense.price || 0),
              quantity: 1,
            },
          ],
        });
        const lic = r.data.licenses || {};
        setLicense(lic.creator?.enabled ? "creator" : "reseller");
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleBuy = async (requestedLicense = license, phoneConfirmed = false) => {
    if (!user) {
      const intentId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(
        "orv_checkout_intent",
        JSON.stringify({
          id: intentId,
          productSlug: slug,
          license: requestedLicense,
        })
      );
      const next = `/products/${slug}?checkout_intent=${encodeURIComponent(
        intentId
      )}`;
      toast.info("Create your account to continue checkout");
      navigate(`/signup?next=${encodeURIComponent(next)}`);
      return;
    }

    if (checkoutLockRef.current) {
      toast.info("Your checkout is already opening.");
      return;
    }

    const checkoutLicense =
      typeof requestedLicense === "string" &&
      product.licenses?.[requestedLicense]?.enabled
        ? requestedLicense
        : license;

    if (!product.licenses?.[checkoutLicense]?.enabled) {
      toast.error("Please select an available licence.");
      return;
    }

    if (!phoneConfirmed) {
      setPendingCheckoutLicense(checkoutLicense);
      setPhoneModalOpen(true);
      return;
    }

    const normalizedPhone = customerPhone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
    if (!/^[6-9]\d{9}$/.test(normalizedPhone)) {
      toast.error("Enter a valid 10-digit Indian mobile number.");
      return;
    }

    checkoutLockRef.current = true;
    setBuying(true);
    let completed = false;

    const resetCheckout = () => {
      checkoutLockRef.current = false;
      setBuying(false);
      window.clearInterval(pollTimerRef.current);
      window.clearTimeout(timeoutTimerRef.current);
      pollTimerRef.current = null;
      timeoutTimerRef.current = null;
    };

    const finishPaidCheckout = (order) => {
      if (completed) return;
      completed = true;
      window.clearInterval(pollTimerRef.current);
      window.clearTimeout(timeoutTimerRef.current);

      cashfreeRef.current = null;
      checkoutLockRef.current = false;
      sessionStorage.removeItem("orv_pending_checkout");

      const item = order.items?.[0] || {};
      trackEvent("Purchase", {
        transaction_id: order.id,
        content_ids: [product.id],
        content_name: product.name,
        content_type: "product",
        value: Number(order.amount || 0),
        currency: order.currency || "INR",
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_category: product.category_name,
            item_variant: item.license_type || checkoutLicense,
            price: Number(order.amount || 0),
            quantity: 1,
          },
        ],
      });

      toast.success("Payment successful! Your product is ready.");
      navigate("/dashboard", { replace: true });
    };

    try {
      trackEvent("InitiateCheckout", {
        content_ids: [product.id],
        content_name: product.name,
        content_type: "product",
        value: Number(product.licenses?.[checkoutLicense]?.price || 0),
        currency: "INR",
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_category: product.category_name,
            item_variant: checkoutLicense,
            price: Number(product.licenses?.[checkoutLicense]?.price || 0),
            quantity: 1,
          },
        ],
      });

      const { data } = await api.post("/checkout/create-order", {
        product_id: product.id,
        license_type: checkoutLicense,
        customer_phone: normalizedPhone,
      });

      if (data.already_owned) {
        sessionStorage.removeItem("orv_pending_checkout");
        resetCheckout();
        toast.success("You already own this licence. Opening My OrvDigital.");
        navigate("/dashboard", { replace: true });
        return;
      }

      if (data.payment_pending_setup) {
        resetCheckout();
        toast.info("Payment gateway setup is still being finalized.");
        return;
      }

      sessionStorage.setItem(
        "orv_pending_checkout",
        JSON.stringify({
          orderId: data.order_id,
          productId: product.id,
          license: checkoutLicense,
        })
      );

      const loaded = await loadCashfree();
      if (!loaded) {
        resetCheckout();
        toast.error("Could not load the secure Cashfree payment window.");
        return;
      }

      const checkServerStatus = async () => {
        if (completed) return;
        try {
          const { data: order } = await api.get(`/checkout/orders/${data.order_id}/status`);
          if (["PAID", "COMPLETED", "PARTIALLY_REFUNDED"].includes(order.status)) {
            finishPaidCheckout(order);
          } else if (["FAILED", "CANCELLED", "REFUNDED", "CHARGEBACK"].includes(order.status)) {
            completed = true;
            resetCheckout();
            sessionStorage.removeItem("orv_pending_checkout");
            toast.error(order.status === "CANCELLED" ? "Payment was cancelled." : "Payment was not completed.");
          }
        } catch {
          // The webhook or next poll can still confirm the payment.
        }
      };

      cashfreeRef.current = window.Cashfree({
        mode: data.environment === "production" ? "production" : "sandbox",
      });

      pollTimerRef.current = window.setInterval(checkServerStatus, 2000);
      timeoutTimerRef.current = window.setTimeout(async () => {
        await checkServerStatus();
        if (!completed) {
          resetCheckout();
          toast.info("Confirmation is taking longer than expected. Check My OrvDigital before paying again.");
        }
      }, 120000);

      const checkoutResult = await cashfreeRef.current.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_modal",
      });
      if (checkoutResult?.error) {
        resetCheckout();
        toast.error(checkoutResult.error.message || "Cashfree could not open checkout.");
        return;
      }
      toast.info("Checking your secure payment status…");
      await checkServerStatus();
    } catch (error) {
      resetCheckout();
      toast.error(
        formatApiError(error, "Could not start checkout. Please try again.")
      );
    }
  };

  useEffect(() => {
    if (!product || !user || autoCheckoutStarted.current) return;

    const intentId = searchParams.get("checkout_intent");
    if (!intentId) return;

    let savedIntent = null;
    try {
      savedIntent = JSON.parse(
        sessionStorage.getItem("orv_checkout_intent") || "null"
      );
    } catch {
      sessionStorage.removeItem("orv_checkout_intent");
    }

    // Consume the intent before opening Cashfree. A mobile-browser remount can
    // therefore never launch the same automatic checkout a second time.
    sessionStorage.removeItem("orv_checkout_intent");
    window.history.replaceState({}, "", `/products/${slug}`);

    if (
      !savedIntent ||
      savedIntent.id !== intentId ||
      savedIntent.productSlug !== slug
    ) {
      toast.info("Tap Get Instant Access to continue your purchase.");
      return;
    }

    autoCheckoutStarted.current = true;
    const requestedLicense = savedIntent.license;
    const checkoutLicense =
      requestedLicense && product.licenses?.[requestedLicense]?.enabled
        ? requestedLicense
        : product.licenses?.creator?.enabled
          ? "creator"
          : "reseller";

    setLicense(checkoutLicense);
    handleBuy(checkoutLicense);
  }, [product, user, searchParams, slug]);



if (loading)
    return (
      <div className="min-h-screen bg-[#050506] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );

  if (!product)
    return (
      <div className="min-h-screen bg-[#050506] flex flex-col items-center justify-center px-4">
        <h1 className="text-3xl font-bold text-white mb-4">Product not found</h1>
        <Link to="/products" className="text-purple-400 hover:text-purple-300">Browse all products</Link>
      </div>
    );

  const lic = product.licenses || {};
  const currentLic = lic[license] || {};
  const gallery = product.gallery?.length ? product.gallery : [product.cover_image];

  return (
    <div className="min-h-screen bg-[#050506] pt-24 pb-32 md:pb-24">
      {phoneModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="checkout-phone-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111114] p-6 shadow-2xl shadow-purple-950/40">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-400">Secure checkout</p>
                <h2 id="checkout-phone-title" className="mt-2 text-2xl font-bold text-white">Complete your purchase</h2>
              </div>
              <button
                type="button"
                onClick={() => setPhoneModalOpen(false)}
                className="rounded-lg p-2 text-gray-400 hover:bg-white/5 hover:text-white"
                aria-label="Close checkout"
              >
                ×
              </button>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-gray-400">
              Enter the mobile number you want to use for secure payment processing.
            </p>
            <label htmlFor="checkout-phone-modal" className="mt-6 block text-sm font-medium text-white">
              Mobile number
            </label>
            <div className="mt-2 flex items-center rounded-xl border border-white/10 bg-white/5 focus-within:border-purple-500/60">
              <span className="pl-4 text-sm text-gray-400">+91</span>
              <input
                id="checkout-phone-modal"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength={10}
                autoFocus
                value={customerPhone}
                onChange={(event) => setCustomerPhone(event.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit mobile number"
                className="w-full bg-transparent px-3 py-3 text-white outline-none placeholder:text-gray-600"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              No separate OrvDigital mobile OTP. Your bank or UPI app may authenticate the payment.
            </p>
            <Button
              type="button"
              onClick={() => {
                const normalized = customerPhone.replace(/\D/g, "");
                if (!/^[6-9]\d{9}$/.test(normalized)) {
                  toast.error("Enter a valid 10-digit Indian mobile number.");
                  return;
                }
                setPhoneModalOpen(false);
                handleBuy(pendingCheckoutLicense || license, true);
              }}
              className="mt-6 w-full rounded-xl bg-gradient-to-r from-purple-600 to-purple-500 py-5 font-semibold text-white"
            >
              Continue to secure payment
            </Button>
            <p className="mt-3 text-center text-xs text-gray-600">Payments are securely processed by Cashfree.</p>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 py-4 md:py-6">
          <Link to="/products" className="hover:text-white">Products</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-400">{product.category_name}</span>
        </div>

        {/* Mobile-only product title (ensures full title is visible on open, below the header) */}
        <div className="lg:hidden mb-6" data-testid="product-title-mobile">
          {product.badge && (
            <span className="inline-block px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full mb-3">
              {product.badge}
            </span>
          )}
          <span className="block text-sm font-medium text-purple-400">{product.category_name}</span>
          <h1 className="text-3xl font-bold text-white mt-2">{product.name}</h1>
        </div>

        {/* Hero: gallery + purchase */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 mb-8">
          {/* Gallery */}
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative rounded-2xl overflow-hidden border border-white/10 bg-purple-900/10 aspect-[4/3]"
            >
              <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-cover" />
            </motion.div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-4">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      activeImg === i ? "border-purple-500" : "border-white/10"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Purchase panel */}
          <div>
            <div className="hidden lg:block">
              {product.badge && (
                <span className="inline-block px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full mb-4">
                  {product.badge}
                </span>
              )}
              <span className="text-sm font-medium text-purple-400">{product.category_name}</span>
              <h1 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">{product.name}</h1>
            </div>
            <p className="text-gray-400 leading-relaxed mb-8 mt-2 lg:mt-0">{product.short_description}</p>

            {/* License selector */}
            <div className="mb-6">
              <p className="text-sm font-semibold text-white mb-3">Choose your license</p>
              <div className="space-y-3" role="radiogroup" aria-label="License options">
                {["creator", "reseller"].map((key) =>
                  lic[key]?.enabled ? (
                    <button
                      key={key}
                      role="radio"
                      aria-checked={license === key}
                      onClick={() => setLicense(key)}
                      data-testid={`license-${key}`}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        license === key
                          ? "border-purple-500 bg-purple-500/10"
                          : "border-white/10 bg-white/5 hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              license === key ? "border-purple-500" : "border-gray-500"
                            }`}
                          >
                            {license === key && <span className="w-2.5 h-2.5 rounded-full bg-purple-500" />}
                          </span>
                          <span className="font-semibold text-white capitalize">{key} License</span>
                        </div>
                        <span className="text-lg font-bold text-white">₹{lic[key].price}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2 ml-8">{lic[key].description}</p>
                    </button>
                  ) : null
                )}
              </div>
            </div>

            {/* Price + CTA */}
            <div className="flex items-end justify-between mb-4">
              <div>
                <p className="text-sm text-gray-400">Total</p>
                <p className="text-4xl font-bold text-white" data-testid="current-price">₹{currentLic.price}</p>
              </div>
              <span className="text-xs text-gray-500 capitalize">{license} License</span>
            </div>

            <Button
              onClick={() => handleBuy()}
              disabled={buying}
              data-testid="buy-now-btn"
              className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-semibold py-6 rounded-xl text-base shadow-xl shadow-purple-500/25 transition-all"
            >
              {buying ? "Processing..." : "Get Instant Access"}
            </Button>

            <div className="grid grid-cols-2 gap-3 mt-6">
              {[
                { icon: Zap, text: "Instant digital access" },
                { icon: ShieldCheck, text: "Secure checkout" },
                { icon: Download, text: "Access from your account" },
                { icon: Users, text: "Creator-focused" },
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-400">
                  <t.icon className="w-4 h-4 text-purple-400" />
                  {t.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content sections */}
        <div className="max-w-4xl">
          {product.highlights?.length > 0 && (
            <Section icon={Sparkles} title="Product Highlights" testid="section-highlights">
              <div className="grid sm:grid-cols-2 gap-3">
                {product.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{h}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {product.full_description && (
            <Section icon={Package} title="About This Product">
              <p className="text-gray-400 leading-relaxed">{product.full_description}</p>
            </Section>
          )}

          {product.whats_included?.length > 0 && (
            <Section icon={Package} title="What's Included">
              <div className="grid sm:grid-cols-2 gap-3">
                {product.whats_included.map((w, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{w}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {product.sample_url && (
  <Section
    icon={Download}
    title="🎁 Download Free Sample"
    testid="section-preview"
  >
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
      <p className="text-sm text-gray-400 mb-4">
        No signup required
      </p>

      <a
        href={product.sample_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="sample-download"
      >
        <Button className="bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-xl px-6 py-3">
          <Download className="w-4 h-4 mr-2" />
          Download Sample
        </Button>
      </a>
    </div>
  </Section>
)}

          {product.content_breakdown?.length > 0 && (
            <Section icon={Layers} title="Content Breakdown">
              <p className="text-sm text-gray-500 -mt-3 mb-5">A closer look at the content categories inside this collection.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {product.content_breakdown.map((c, i) => (
                  <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <h4 className="font-semibold text-white mb-1">{c.title}</h4>
                    <p className="text-sm text-gray-400">{c.description}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {product.who_its_for?.length > 0 && (
            <Section icon={Users} title="Who It's For">
              <div className="flex flex-wrap gap-2">
                {product.who_its_for.map((w, i) => (
                  <span key={i} className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-gray-300">
                    {w}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {product.how_it_works?.length > 0 && (
            <Section icon={Zap} title="How It Works">
              <div className="space-y-4">
                {product.how_it_works.map((step, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-purple-500/15 text-purple-400 font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-gray-300 pt-1">{step}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section icon={ShieldCheck} title="License Rights">
            <div className="p-6 rounded-xl bg-white/5 border border-white/10">
              <p className="text-sm text-purple-400 font-semibold capitalize mb-3">{license} License includes:</p>
              <div className="space-y-2">
                {(currentLic.rights || []).map((r, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {product.delivery_info && (
            <Section icon={Download} title="Delivery Information">
              <p className="text-gray-400 leading-relaxed">{product.delivery_info}</p>
            </Section>
          )}

          {product.faq?.length > 0 && (
            <Section icon={HelpCircle} title="Frequently Asked Questions">
              <Accordion type="single" collapsible className="space-y-3">
                {product.faq.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-white/5 border border-white/10 rounded-xl px-5">
                    <AccordionTrigger className="text-white hover:text-purple-300 hover:no-underline text-left">
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-400">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Section>
          )}

          {product.related?.length > 0 && (
            <Section icon={Package} title="Related Products">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {product.related.map((r, i) => (
                  <ProductCard key={r.id} product={r} index={i} />
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-[#0B0B0D]/95 backdrop-blur-xl border-t border-white/10 p-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs text-gray-400 capitalize">{license} License</p>
          <p className="text-2xl font-bold text-white">₹{currentLic.price}</p>
        </div>
        <Button
          onClick={() => handleBuy()}
          disabled={buying}
          data-testid="buy-now-mobile"
          className="flex-1 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold py-5 rounded-xl"
        >
          {buying ? "Processing..." : "Get Access"}
        </Button>
      </div>
    </div>
  );
}
